import { Loader2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import axiosWithTenant from '../utils/axiosHandler';
import './PaymentMethodsManagement.css';

// Card form component using Square
const AddCardForm = ({ onSuccess, onCancel, squareConfig }) => {
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [nickname, setNickname] = useState('');
    const [setAsDefault, setSetAsDefault] = useState(false);
    const paymentFormRef = useRef(null);

    useEffect(() => {
        if (!squareConfig) return;

        // Load Square Web Payments SDK
        const script = document.createElement('script');
        if (squareConfig.environment === 'sandbox') {
            script.src = 'https://sandbox.web.squarecdn.com/v1/square.js';
        } else {
            script.src = 'https://web.squarecdn.com/v1/square.js';
        }

        script.async = true;
        script.onload = () => {
            setTimeout(initializeSquare, 100);
        };
        script.onerror = () => {
            setError('Failed to load payment system');
            setLoading(false);
        };
        document.body.appendChild(script);

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, [squareConfig]);

    const initializeSquare = async () => {
        if (!window.Square) {
            setError('Payment system not available');
            setLoading(false);
            return;
        }

        try {
            setError('');

            // Ensure the container element exists
            const container = document.getElementById('add-card-container');
            if (!container) {
                await new Promise(resolve => setTimeout(resolve, 200));
                const retryContainer = document.getElementById('add-card-container');
                if (!retryContainer) {
                    setError('Payment form container not ready');
                    setLoading(false);
                    return;
                }
            }

            // Minimal logging - only in development mode
            if (process.env.NODE_ENV === 'development') {
                console.log('Initializing Square with environment:', squareConfig.environment);
            }

            const payments = window.Square.payments(
                squareConfig.applicationId,
                squareConfig.locationId
            );

            const cardOptions = {};
            const card = await payments.card(cardOptions);
            await card.attach('#add-card-container');

            paymentFormRef.current = { card, payments };
            setError('');
            setLoading(false);

            if (process.env.NODE_ENV === 'development') {
                console.log('Square card form initialized successfully');
            }
        } catch (err) {
            console.error('Square initialization error:', err);
            setError(`Failed to initialize payment form: ${err.message || 'Unknown error'}`);
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!paymentFormRef.current) {
            setError('Payment form not ready');
            return;
        }

        setProcessing(true);
        setError(null);

        try {
            const { card } = paymentFormRef.current;

            // Tokenize the card
            const tokenResult = await card.tokenize();

            if (tokenResult.status === 'OK') {
                // Send payment method to backend
                const response = await axiosWithTenant.post('/api/payment-methods', {
                    cardToken: tokenResult.token,
                    setAsDefault,
                    nickname
                });

                if (response.data.success) {
                    onSuccess();
                } else {
                    throw new Error(response.data.message);
                }
            } else {
                let errorMessage = 'Payment failed';
                if (tokenResult.errors && tokenResult.errors.length > 0) {
                    errorMessage = tokenResult.errors.map(error => error.message).join(', ');
                }
                throw new Error(errorMessage);
            }
        } catch (err) {
            console.error('Error adding card:', err);
            setError(err.response?.data?.message || err.message || 'Failed to add card');
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                <span className="ml-3 text-gray-600">Loading payment form...</span>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="add-card-form">
            <div className="form-group">
                <label htmlFor="nickname">Card Nickname (Optional)</label>
                <input
                    type="text"
                    id="nickname"
                    placeholder="e.g., Personal Card, Business Amex"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    className="form-input"
                    maxLength={50}
                />
                {nickname && nickname.length > 40 && (
                    <small className="text-gray-500">{nickname.length}/50 characters</small>
                )}
            </div>

            <div className="form-group">
                <label>Card Information</label>
                <div id="add-card-container" className="card-element-wrapper"></div>
            </div>

            <div className="form-group checkbox-group">
                <label>
                    <input
                        type="checkbox"
                        checked={setAsDefault}
                        onChange={(e) => setSetAsDefault(e.target.checked)}
                    />
                    <span>Set as default payment method</span>
                </label>
            </div>

            {error && (
                <div className="error-message">
                    <i className="fas fa-exclamation-circle"></i>
                    {error}
                </div>
            )}

            <div className="form-actions">
                <button
                    type="button"
                    onClick={onCancel}
                    className="btn btn-secondary"
                    disabled={processing}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={processing || loading}
                >
                    {processing ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                            Adding...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-plus"></i> Add Card
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

// Payment methods list component
const PaymentMethodsManagement = () => {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [squareConfig, setSquareConfig] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        initializeSquare();
        fetchPaymentMethods();
    }, []);

    const initializeSquare = async () => {
        try {
            if (process.env.NODE_ENV === 'development') {
                console.log('Fetching Square config...');
            }

            const response = await axiosWithTenant.get('/api/subscriptions/square-config');

            if (process.env.NODE_ENV === 'development') {
                console.log('Square config received');
            }

            if (response.data && response.data.applicationId) {
                setSquareConfig(response.data);
            } else {
                console.error('Invalid Square config received');
                setError('Square is not configured properly');
            }
        } catch (err) {
            console.error('Error initializing Square:', err);
            setError('Failed to initialize payment system: ' + (err.response?.data?.message || err.message));
        }
    };

    const fetchPaymentMethods = async () => {
        try {
            setLoading(true);
            const response = await axiosWithTenant.get('/api/payment-methods');
            if (response.data.success) {
                setPaymentMethods(response.data.paymentMethods);
            }
        } catch (err) {
            console.error('Error fetching payment methods:', err);
            setError('Failed to load payment methods');
        } finally {
            setLoading(false);
        }
    };

    const handleSetDefault = async (paymentMethodId) => {
        try {
            const response = await axiosWithTenant.put(`/api/payment-methods/${paymentMethodId}/default`);
            if (response.data.success) {
                fetchPaymentMethods();
            }
        } catch (err) {
            console.error('Error setting default payment method:', err);
            alert(err.response?.data?.message || 'Failed to set default payment method');
        }
    };

    const handleRemove = async (paymentMethodId) => {
        // Check if this is the last payment method
        if (paymentMethods.length <= 1) {
            alert('You must keep at least one payment method saved for auto-renewing subscriptions. Please add another payment method before removing this one.');
            return;
        }

        if (!window.confirm('Are you sure you want to remove this payment method?')) {
            return;
        }

        try {
            const response = await axiosWithTenant.delete(`/api/payment-methods/${paymentMethodId}`);
            if (response.data.success) {
                fetchPaymentMethods();
            }
        } catch (err) {
            console.error('Error removing payment method:', err);
            const errorMessage = err.response?.data?.message || 'Failed to remove payment method';
            alert(errorMessage);
        }
    };

    const handleAddSuccess = () => {
        setShowAddForm(false);
        fetchPaymentMethods();
    };

    const getCardIcon = (brand) => {
        const icons = {
            visa: 'fab fa-cc-visa',
            mastercard: 'fab fa-cc-mastercard',
            amex: 'fab fa-cc-amex',
            discover: 'fab fa-cc-discover',
            diners: 'fab fa-cc-diners-club',
            jcb: 'fab fa-cc-jcb',
        };
        return icons[brand?.toLowerCase()] || 'fas fa-credit-card';
    };

    if (error && !squareConfig) {
        return (
            <div className="payment-methods-container">
                <div className="error-state">
                    <i className="fas fa-exclamation-triangle"></i>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="payment-methods-container">
            <div className="payment-methods-header">
                <h2>
                    <i className="fas fa-credit-card"></i> Payment Methods
                </h2>
                {!showAddForm && (
                    <button
                        onClick={() => setShowAddForm(true)}
                        className="btn btn-primary"
                    >
                        <i className="fas fa-plus"></i> Add Payment Method
                    </button>
                )}
            </div>

            {showAddForm && squareConfig && (
                <div className="add-card-section">
                    <h3>Add New Payment Method</h3>
                    <AddCardForm
                        onSuccess={handleAddSuccess}
                        onCancel={() => setShowAddForm(false)}
                        squareConfig={squareConfig}
                    />
                </div>
            )}

            {loading ? (
                <div className="loading-state">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>Loading payment methods...</p>
                </div>
            ) : paymentMethods.length === 0 ? (
                <div className="empty-state">
                    <i className="fas fa-credit-card"></i>
                    <p>No payment methods saved</p>
                    <p className="subtitle">Add a payment method to manage your subscription</p>
                </div>
            ) : (
                <div className="payment-methods-list">
                    {paymentMethods.map((method) => (
                        <div
                            key={method.id}
                            className={`payment-method-card ${method.isDefault ? 'default' : ''} ${method.isExpired ? 'expired' : ''}`}
                        >
                            <div className="card-icon">
                                <i className={getCardIcon(method.brand)}></i>
                            </div>
                            <div className="card-details">
                                <div className="card-info">
                                    <span className="card-brand">{method.brand.toUpperCase()}</span>
                                    <span className="card-number">•••• {method.last4}</span>
                                </div>
                                <div className="card-meta">
                                    {method.nickname && (
                                        <span className="card-nickname">{method.nickname}</span>
                                    )}
                                    <span className="card-expiry">
                                        Expires {method.expMonth}/{method.expYear}
                                    </span>
                                </div>
                                {method.isExpired && (
                                    <div className="expired-badge">
                                        <i className="fas fa-exclamation-triangle"></i> Expired
                                    </div>
                                )}
                                {method.isDefault && (
                                    <div className="default-badge">
                                        <i className="fas fa-check-circle"></i> Default
                                    </div>
                                )}
                            </div>
                            <div className="card-actions">
                                {!method.isDefault && !method.isExpired && (
                                    <button
                                        onClick={() => handleSetDefault(method.id)}
                                        className="btn-icon"
                                        title="Set as default"
                                    >
                                        <i className="fas fa-star"></i>
                                    </button>
                                )}
                                <button
                                    onClick={() => handleRemove(method.id)}
                                    className="btn-icon btn-danger"
                                    title="Remove"
                                >
                                    <i className="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="payment-info">
                <i className="fas fa-info-circle"></i>
                <p>
                    <strong>Note:</strong> At least one payment method must be saved for auto-renewing subscriptions.
                    Your default payment method will be charged automatically when your subscription renews.
                </p>
            </div>
        </div>
    );
};

export default PaymentMethodsManagement;

